import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiFileText,
  FiSearch,
  FiCheckCircle,
  FiPlus,
  FiExternalLink,
  FiList,
  FiX,
  FiUpload,
  FiPaperclip,
  FiDollarSign,
  FiAlertCircle,
  FiUser,
  FiBriefcase,
  FiChevronLeft,
  FiEdit2,
} from "react-icons/fi";
import API from "../utils/api";
import TdsFields, { EMPTY_TDS } from '../components/common/TdsFields';
import { resolveTds, allocationsPayload, tdsPayload, fmtPercent, billSettlement, overflowMessage } from '../utils/tds';
import toast from "react-hot-toast";
import { useStaffLog } from "../utils/useStaffLog";
import { MdCurrencyRupee } from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import EditReceiptModal from "../components/common/EditReceiptModal";
import SettleOpeningBalanceModal from "../components/admin/SettleOpeningBalanceModal";
import ReconcileModal from "../components/common/ReconcileModal";
import "./SalesOrders.css";
import "./AdminBilling.css";

/* ── Helpers ─────────────────────────────────────────────────────── */
const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const BILL_STATUS = {
  generated: { label: "Unpaid", color: "#dc2626", bg: "#fee2e2" },
  partial: { label: "Partial", color: "#d97706", bg: "#fef3c7" },
  paid: { label: "Paid", color: "#16a34a", bg: "#dcfce7" },
  cancelled: { label: "Cancelled", color: "#4b5563", bg: "#e5e7eb" },
};

/* Resolve a stored pdfUrl (may be a bare /uploads/… path) to a full URL
   served by the backend, so it works regardless of the frontend origin. */
const API_ORIGIN = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

const resolvePdfUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: 4,
};
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  fontSize: 14,
  outline: "none",
};

/* ── Consolidation logic ─────────────────────────────────────────── */
/* Partial billing — stable line id + "is this line going on the bill?" rule.
 * A line is included when it is NOT already billed on another bill AND
 * (no per-item selection was made for the order OR the line is ticked). */
const lineKey = (item, idx) =>
  String(item && item.id != null && item.id !== "" ? item.id : `L${idx + 1}`);

const isLineIncluded = (order, item, idx, selectedItemMap, billedLines) => {
  const key = lineKey(item, idx);
  if (billedLines && billedLines[order.id] && billedLines[order.id][key]) return false;
  const imap = selectedItemMap && selectedItemMap[order.id];
  if (imap && !imap.has(item.id)) return false;
  return true;
};

const consolidateItems = (selectedOrders, selectedItemMap, billedLines) => {
  const map = new Map();

  for (const order of selectedOrders) {
    const items = Array.isArray(order.items) ? order.items : [];
    for (let idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      if (!isLineIncluded(order, item, idx, selectedItemMap, billedLines)) continue;

      const key = `${String(item.product || "")
        .trim()
        .toLowerCase()}||${Number(item.price || 0).toFixed(2)}||${Number(item.taxPercent || 0)}`;
      const existing = map.get(key);
      const qty = Number(item.qty) || 0;
      const lineTotal = qty * Number(item.price || 0);

      if (existing) {
        existing.totalQty += qty;
        existing.lineTotal += lineTotal;
        existing.sourceOrders.push({
          orderId: order.id,
          orderNumber: order.orderNumber,
          qty,
        });
      } else {
        map.set(key, {
          product: String(item.product || "").trim(),
          unitPrice: Number(item.price || 0),
          unit: item.unit || "",
          taxPercent: Number(item.taxPercent || 0),
          totalQty: qty,
          lineTotal,
          sourceOrders: [
            { orderId: order.id, orderNumber: order.orderNumber, qty },
          ],
        });
      }
    }
  }

  return Array.from(map.values());
};

/* ── Record Payment Modal (Bill-linked ClientReceipt) ───────────────
 * Replaces RecordPaymentModal. Calls POST /api/client-receipts with
 * billAllocations so the payment is visible in the customer ledger.
 * Receipt number is auto-generated server-side (R00001…).
 * ──────────────────────────────────────────────────────────────────── */
const RecordPaymentModal = ({ bill, onClose, onSaved }) => {
  const [form, setForm]               = useState({ amount: '', date: new Date().toISOString().slice(0, 10), mode: 'cash', bankAccountId: '', note: '' });
  const [saving, setSaving]           = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nextNumber, setNextNumber]   = useState('');
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
    API.get('/client-receipts/next-number')
      .then(({ data }) => setNextNumber(data.receiptNumber || ''))
      .catch(() => {});
  }, []);

  const balance = Number(bill.grandTotal) - Number(bill.amountReceived);

  // TDS (optional): % and amount are linked — see utils/tds.js
  const [tdsInput, setTdsInput] = useState(EMPTY_TDS);
  const resolved  = resolveTds({ bills: [bill], effective: Number(form.amount) || 0, mode: 'fifo', input: tdsInput });
  const settleNow = (Number(form.amount) || 0) + resolved.amount;
  // Save is blocked when TDS cannot be saved as entered, or payment + TDS is more than the balance
  const blocked   = !!resolved.error || (resolved.amount > 0 && settleNow > Number(bill.grandTotal) - Number(bill.amountReceived) + 0.01);

  const handleSave = async () => {
    const amt = Number(form.amount) || 0;
    if (amt < 0) { toast.error('Enter a valid amount'); return; }
    if (amt <= 0 && resolved.amount <= 0) { toast.error('Enter the amount received or the TDS deducted'); return; }
    if (resolved.error) { toast.error(resolved.error); return; }
    if (settleNow > balance + 0.01) {
      toast.error(resolved.amount > 0
        ? `Payment ${money(amt)} + TDS ${money(resolved.amount)} is ${money(settleNow - balance)} more than the balance (${money(balance)}). If the customer deducted TDS, enter ${money(resolved.cashNeeded)} as the amount received.`
        : `Exceeds balance (max ${money(balance)} including TDS)`);
      return;
    }
    try {
      setSaving(true);
      await API.post('/client-receipts', {
        businessId:       bill.businessId || null,
        customerName:     bill.customerName || '',
        customerPhone:    bill.customerPhone || '',
        companyName:      bill.companyName || '',
        totalAmount:      amt,
        creditUsed:       0,
        receiptDate:      form.date,
        mode:             form.mode,
        bankAccountId:    form.bankAccountId || null,
        bankAccountLabel: bankAccounts.find(a => String(a.id) === String(form.bankAccountId))?.account_name || null,
        note:             form.note,
        billAllocations:  allocationsPayload(resolved.plan),
        ...tdsPayload(resolved, tdsInput, tdsInput.section),
      });
      toast.success(`Payment recorded — Receipt ${nextNumber}`);
      // Reload the bill so the table shows updated received/balance
      const { data } = await API.get(`/bills/${bill.id}`);
      onSaved(data.bill || bill);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Record Payment</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
          {bill.billNumber} · {bill.companyName || bill.customerName}
        </div>
        {nextNumber && (
          <div style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, fontSize: 13 }}>
            Receipt No: <strong style={{ color: '#16a34a' }}>{nextNumber}</strong>
            <span style={{ fontSize: 11, color: '#6b7280' }}>(auto-assigned)</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13 }}>
          <span style={{ color: '#16a34a' }}>Settled: <strong>{money(bill.amountReceived)}</strong>{billSettlement(bill).tds > 0 && <span style={{ color: '#b45309' }}> (incl. TDS {money(billSettlement(bill).tds)})</span>}</span>
          <span style={{ color: '#dc2626' }}>Balance: <strong>{money(balance)}</strong></span>
          <span style={{ color: '#111' }}>Total: <strong>{money(bill.grandTotal)}</strong></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Amount Received (₹) *</label>
            <input type="number" min="0" max={balance} step="0.01"
              value={form.amount} onChange={e => set('amount', e.target.value)}
              placeholder={`Max ${money(balance)}`} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
              onClick={e => e.currentTarget.showPicker?.()} style={{ ...inputStyle, cursor: 'pointer' }} />
          </div>
          <div>
            <label style={labelStyle}>Mode</label>
            <select value={form.mode} onChange={e => { set('mode', e.target.value); set('bankAccountId', ''); }} style={inputStyle}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <input type="text" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Received In Account</label>
            <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)} style={inputStyle}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter(a => {
                  if (form.mode === 'cash')         return a.account_type === 'cash';
                  if (form.mode === 'upi')          return a.account_type === 'upi';
                  if (form.mode === 'bank_transfer' || form.mode === 'cheque') return a.account_type === 'bank';
                  return true;
                })
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
        </div>
        <TdsFields input={tdsInput} onChange={setTdsInput} resolved={resolved} overflowMode="block" />
        {settleNow > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: settleNow > balance + 0.01 ? '#dc2626' : '#374151' }}>
            Settles <strong>{money(settleNow)}</strong> of this bill
            {resolved.amount > 0 && <> (bank {money(Number(form.amount) || 0)} + TDS {money(resolved.amount)})</>}
            {' '}· balance after: <strong>{money(Math.max(0, balance - settleNow))}</strong>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || blocked} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: (saving || blocked) ? 'not-allowed' : 'pointer', opacity: (saving || blocked) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Payment History Modal ───────────────────────────────────────── */
const PaymentHistoryModal = ({ bill, onClose }) => {
  const entries = Array.isArray(bill.paymentEntries) ? bill.paymentEntries : [];
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 28,
          width: 760,
          maxWidth: "95vw",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>
              Payment History
            </h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 3 }}>
              {bill.billNumber} · {bill.companyName || bill.customerName}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            <FiX size={18} />
          </button>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {entries.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 0",
                color: "#9ca3af",
                fontSize: 13,
              }}
            >
              No payment entries yet.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 620,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>Date</th>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600 }}>Amount</th>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600, minWidth: 120 }}>Receipt No</th>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600 }}>Mode</th>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600 }}>Account</th>
                  <th style={{ textAlign: "left", padding: "6px 12px", color: "#6b7280", fontWeight: 600 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => {
                  const isReceipt = e.source === "receipt";
                  const isCredit  = e.source === "credit";
                  const isDirect  = !isReceipt && !isCredit;

                  const sourceBadge = isDirect ? (
                    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: "#f3f4f6", color: "#6b7280" }}>
                      —
                    </span>
                  ) : isReceipt ? (
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: "#ede9fe", color: "#6d28d9", whiteSpace: "nowrap", letterSpacing: "0.02em" }}>
                      {e.receiptNumber || "—"}
                    </span>
                  ) : (
                    <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: "#fef3c7", color: "#b45309", whiteSpace: "nowrap" }}>
                      {e.receiptNumber || "—"}
                    </span>
                  );

                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: isCredit ? "#fffbeb" : isReceipt ? "#faf5ff" : "#fff" }}>
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#374151" }}>{fmt(e.date)}</td>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: "#16a34a" }}>{money(e.amount)}</td>
                      <td style={{ padding: "10px 12px" }}>{sourceBadge}</td>
                      <td style={{ padding: "10px 12px", textTransform: "capitalize", color: "#374151" }}>
                        {e.mode === "credit_wallet" ? "—" : e.mode}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#374151" }}>{e.bank_account_label || "—"}</td>
                      <td style={{ padding: "10px 12px", color: "#9ca3af" }}>{e.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid #f3f4f6",
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
          }}
        >
          <span style={{ color: "#6b7280" }}>
            Total settled:{" "}
            <strong style={{ color: "#16a34a" }}>
              {money(bill.amountReceived)}
            </strong>
            {billSettlement(bill).tds > 0 && (
              <span style={{ color: "#b45309" }}>
                {" "}(incl. TDS {money(billSettlement(bill).tds)})
              </span>
            )}
          </span>
          <span style={{ color: "#6b7280" }}>
            Balance:{" "}
            <strong
              style={{ color: bill.status !== "paid" ? "#dc2626" : "#6b7280" }}
            >
              {money(
                Math.max(
                  0,
                  Number(bill.grandTotal) - Number(bill.amountReceived),
                ),
              )}
            </strong>
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 14,
            padding: "9px 20px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#f9fafb",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            alignSelf: "flex-end",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

/* ── Customer Search (autocomplete) ─────────────────────────────── */
const CustomerCombobox = ({ selectedCustomer, onSelect, activeTab }) => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch]       = useState("");
  const [open, setOpen]           = useState(false);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  /* Generate tab: load customer list from sales orders (unchanged) */
  const load = useCallback(async () => {
    if (activeTab !== "generate") return;
    try {
      const params = new URLSearchParams({ limit: 500 });
      const [{ data }, bizRes] = await Promise.all([
        API.get(`/sales-orders?${params}`),
        API.get("/business?limit=500"),
      ]);
      const items = data.orders || [];
      const bizMonthlyMap = new Map();
      (bizRes.data?.businesses || []).forEach((b) => {
        bizMonthlyMap.set(b.id, !!b.monthlyBillingEnabled);
      });
      const map = new Map();
      for (const item of items) {
        const key = item.businessId
          ? `biz_${item.businessId}`
          : `cust_${item.customerPhone || item.customerName}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            type: item.businessId ? "business" : "customer",
            name: item.companyName || item.customerName,
            subName: item.companyName ? item.customerName : item.customerPhone,
            phone: item.customerPhone,
            businessId: item.businessId || null,
            isMonthlyBilling: item.businessId ? (bizMonthlyMap.get(item.businessId) ?? false) : false,
            orderCount: 1,
            lastDate: item.orderDate || item.billDate,
          });
        } else {
          const existing = map.get(key);
          existing.orderCount += 1;
          const d = item.orderDate || item.billDate;
          if (d && d > existing.lastDate) existing.lastDate = d;
        }
      }
      setCustomers(Array.from(map.values()).sort((a, b) => (b.lastDate || "") - (a.lastDate || "")));
    } catch {
      toast.error("Failed to load customers");
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  /* All Bills tab: search customers on the server across ALL bills (debounced) */
  useEffect(() => {
    if (activeTab !== "all") return;
    const term = search.trim();
    if (!term) { setCustomers([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get("/bills/customers", { params: { q: term } });
        if (!cancelled) setCustomers(data.customers || []);
      } catch {
        if (!cancelled) toast.error("Failed to search customers");
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, activeTab]);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        if (!selectedCustomer) setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [selectedCustomer]);

  /* Highlight matching part of text */
  const highlight = (text, q) => {
    if (!q || !text) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "#dbeafe", color: "#1d4ed8", padding: 0, borderRadius: 2 }}>
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const q = search.trim().toLowerCase();
  const filtered = activeTab === "all"
    ? (q ? customers : [])
    : q
    ? customers.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.subName?.toLowerCase().includes(q)
      )
    : [];

  const businesses = filtered.filter((c) => c.type === "business");
  const individuals = filtered.filter((c) => c.type === "customer");

  const handleSelect = (c) => {
    onSelect(c);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    setOpen(true);
  };

  const handleInputFocus = () => {
    if (search) setOpen(true);
  };

  return (
    <div ref={wrapRef} className="ab-search">
      <div className={`ab-search__box${open && q ? " ab-search__box--open" : ""}${selectedCustomer ? " ab-search__box--selected" : ""}`}>
        <FiSearch size={15} className="ab-search__icon" />

        {/* Selected chip */}
        {selectedCustomer && (
          <div className="ab-search__chip">
            <span className={`ab-search__chip-dot ab-search__chip-dot--${selectedCustomer.type === "business" ? "biz" : "user"}`} />
            <span className="ab-search__chip-name">{selectedCustomer.name}</span>
            {selectedCustomer.isMonthlyBilling && (
              <span className="ab-search__monthly-tag">MONTHLY</span>
            )}
            <button className="ab-search__chip-rm" onClick={handleClear} title="Clear filter">
              <FiX size={11} />
            </button>
          </div>
        )}

        <input
          ref={inputRef}
          className="ab-search__input"
          placeholder={selectedCustomer ? "" : "Search customer or business…"}
          value={search}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />

        {search && (
          <button className="ab-search__clear-input" onClick={() => { setSearch(""); setOpen(false); }}>
            <FiX size={13} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && q && (
        <div className="ab-search__dropdown">
          {filtered.length === 0 ? (
            <div className="ab-search__empty">No results for "<strong>{search}</strong>"</div>
          ) : (
            <>
              {businesses.length > 0 && (
                <div className="ab-search__group">
                  <div className="ab-search__group-label">Businesses</div>
                  {businesses.map((c) => (
                    <button key={c.key} className={`ab-search__item${selectedCustomer?.key === c.key ? " ab-search__item--active" : ""}`} onClick={() => handleSelect(c)}>
                      <span className="ab-search__item-icon ab-search__item-icon--biz">
                        <FiBriefcase size={12} />
                      </span>
                      <span className="ab-search__item-body">
                        <span className="ab-search__item-name">
                          {highlight(c.name, search)}
                          {c.isMonthlyBilling && <span className="ab-search__monthly-tag" style={{ marginLeft: 6 }}>MONTHLY</span>}
                        </span>
                        <span className="ab-search__item-sub">{highlight(c.subName, search)}</span>
                      </span>
                      <span className="ab-search__item-count">{c.orderCount}</span>
                    </button>
                  ))}
                </div>
              )}

              {businesses.length > 0 && individuals.length > 0 && (
                <div className="ab-search__divider" />
              )}

              {individuals.length > 0 && (
                <div className="ab-search__group">
                  <div className="ab-search__group-label">Customers</div>
                  {individuals.map((c) => (
                    <button key={c.key} className={`ab-search__item${selectedCustomer?.key === c.key ? " ab-search__item--active" : ""}`} onClick={() => handleSelect(c)}>
                      <span className="ab-search__item-icon ab-search__item-icon--user">
                        <FiUser size={12} />
                      </span>
                      <span className="ab-search__item-body">
                        <span className="ab-search__item-name">{highlight(c.name, search)}</span>
                        <span className="ab-search__item-sub">{highlight(c.subName, search)}</span>
                      </span>
                      <span className="ab-search__item-count">{c.orderCount}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="ab-search__footer">
                {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "<strong>{search}</strong>"
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Generate Bill Section ───────────────────────────────────────── */
const GenerateBillSection = ({ onBillCreated, selectedCustomer }) => {
  const { log } = useStaffLog();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoading] = useState(true);
  const [selectedOrders, setSelected] = useState(new Set());
  const [selectedItemMap, setItemMap] = useState({});
  // Partial billing: { [orderId]: { [itemLineId]: billNumber } } — lines already billed
  const [billedLines, setBilledLines] = useState({});
  const [step, setStep] = useState(1);
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const pdfRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [grandTotalOverride, setGrandTotalOverride] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [billNumError, setBillNumError] = useState("");
  const [billNumChecking, setChecking] = useState(false);

  // PO fields
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [poPdfFile, setPoPdfFile] = useState(null);
  const [poUploading, setPoUploading] = useState(false);
  const [poParsing, setPoParsing] = useState(false);
  const [poParseResult, setPoParseResult] = useState(null);
  const [poNumError, setPoNumError] = useState("");
  const [poNumChecking, setPoChecking] = useState(false);
  const poPdfRef = useRef();

  const [hiddenNoTax, setHiddenNoTax] = useState(0);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Server now filters unbilled (+ business) in SQL, and we page through
      // ALL results instead of stopping at the first 200.
      const PAGE = 200;
      const MAX_PAGES = 50;
      let all = [];
      let page = 1;
      let pages = 1;
      do {
        const params = new URLSearchParams({
          limit: PAGE,
          page,
          billingStatus: "unbilled",
        });
        if (selectedCustomer?.businessId)
          params.set("businessId", selectedCustomer.businessId);
        else if (selectedCustomer?.phone)
          params.set("q", selectedCustomer.phone);
        const { data } = await API.get(`/sales-orders?${params}`);
        all = all.concat(data.orders || []);
        pages = data.pages || 1;
        page += 1;
      } while (page <= pages && page <= MAX_PAGES);

      let candidates = all.filter(
        (o) =>
          o.status !== "cancelled" &&
          (o.billingStatus !== "billed" || o.billingStatus == null),
      );
      if (selectedCustomer) {
        candidates = candidates.filter((o) => {
          if (selectedCustomer.businessId)
            return o.businessId === selectedCustomer.businessId;
          return (
            o.customerPhone === selectedCustomer.phone ||
            o.customerName === selectedCustomer.name
          );
        });
      }
      const taxable = candidates.filter(
        (o) =>
          Array.isArray(o.items) &&
          o.items.some((item) => Number(item.taxPercent || 0) > 0),
      );
      setHiddenNoTax(candidates.length - taxable.length);
      setOrders(taxable);

      // Which lines of partially billed orders are already on another bill?
      const partialIds = taxable
        .filter((o) => o.billingStatus === "partial")
        .map((o) => o.id);
      if (partialIds.length) {
        try {
          const { data: bl } = await API.get(
            `/bills/billed-lines?orderIds=${partialIds.join(",")}`,
          );
          const map = {};
          Object.entries(bl.lines || {}).forEach(([orderId, rows]) => {
            map[orderId] = {};
            rows.forEach((r) => {
              map[orderId][r.itemLineId] = r.billNumber || `#${r.billId}`;
            });
          });
          setBilledLines(map);
        } catch {
          setBilledLines({});
        }
      } else {
        setBilledLines({});
      }
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    loadOrders();
  }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => loadOrders(), 350);
    return () => clearTimeout(t);
  }, [selectedCustomer]); // eslint-disable-line

  // Selecting an order also expands its item list (expanded === selected).
  // Unselecting clears any per-item picks so re-selecting starts fresh.
  const toggleOrder = (id) => {
    const wasSelected = selectedOrders.has(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (wasSelected) {
      setItemMap((prev) => {
        if (!(id in prev)) return prev;
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const toggleItem = (orderId, itemId) => {
    setItemMap((prev) => {
      const orderItems = new Set(prev[orderId] || []);
      if (orderItems.has(itemId)) orderItems.delete(itemId);
      else orderItems.add(itemId);
      return { ...prev, [orderId]: orderItems };
    });
  };

  const pickedOrders = orders.filter((o) => selectedOrders.has(o.id));

  const sameCustomer =
    pickedOrders.length <= 1 ||
    pickedOrders.every(
      (o) =>
        (o.businessId && o.businessId === pickedOrders[0].businessId) ||
        o.customerPhone === pickedOrders[0].customerPhone,
    );

  const consolidated =
    step === 2 ? consolidateItems(pickedOrders, selectedItemMap, billedLines) : [];

  // Lines that will actually be billed (sent to the server)
  const billLines = [];
  pickedOrders.forEach((o) => {
    (Array.isArray(o.items) ? o.items : []).forEach((item, idx) => {
      if (isLineIncluded(o, item, idx, selectedItemMap, billedLines)) {
        billLines.push({ orderId: o.id, itemLineId: lineKey(item, idx) });
      }
    });
  });
  const billOrders = pickedOrders.filter((o) =>
    billLines.some((l) => l.orderId === o.id),
  );

  const totals = consolidated.reduce(
    (acc, item) => {
      const taxable = item.lineTotal;
      const tax = taxable * (item.taxPercent / 100);
      acc.taxable += taxable;
      acc.tax += tax;
      acc.grand += taxable + tax;
      return acc;
    },
    { taxable: 0, tax: 0, grand: 0 },
  );

  // Round off at bill level — one round off on the combined raw total
  const rawGrand      = totals.grand; // taxable + tax, unrounded
  const roundedGrand  = Math.round(rawGrand);
  totals.roundOff     = Number((roundedGrand - rawGrand).toFixed(2));
  totals.grand        = roundedGrand;

  const handleCreate = async () => {
    if (!billNumber.trim()) {
      toast.error("Enter a bill number");
      return;
    }
    if (consolidated.length === 0 || billLines.length === 0) {
      toast.error("No items to bill");
      return;
    }
    try {
      setSaving(true);
      let pdfUrl = null;
      if (pdfFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("pdf", pdfFile);
        const { data: upData } = await API.post("/upload/bill-pdf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        pdfUrl = upData.url;
        setUploading(false);
      }

      // Upload PO PDF if provided
      let poPdfUrl = null;
      if (poPdfFile) {
        setPoUploading(true);
        const fd2 = new FormData();
        fd2.append("pdf", poPdfFile);
        const { data: poUpData } = await API.post("/upload/bill-pdf", fd2, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        poPdfUrl = poUpData.url;
        setPoUploading(false);
      }

      const ref = billOrders[0] || pickedOrders[0];
      const { data } = await API.post("/bills", {
        billNumber: billNumber.trim(),
        // Partial billing: the server rebuilds items + totals from these lines
        lines: billLines,
        linkedOrderIds: billOrders.map((o) => o.id),
        customerName: ref.customerName,
        customerPhone: ref.customerPhone,
        customerEmail: ref.customerEmail,
        companyName: ref.companyName,
        gstNo: ref.gstNo,
        companyAddress: ref.companyAddress,
        businessId: ref.businessId || null,
        grandTotal: grandTotalOverride ?? totals.grand,
        grandTotalOverride: grandTotalOverride != null,
        billDate,
        notes,
        pdfUrl,
        poNumber: poNumber.trim() || null,
        poDate: poDate || null,
        poPdfUrl,
      });
      toast.success(`Bill ${data.bill.billNumber} created`);
      log('created_invoice', `Created invoice ${data.bill.billNumber}`, 'Bill', data.bill.id);
      setSelected(new Set());
      setItemMap({});
      setBilledLines({});
      setStep(1);
      setBillNumber("");
      setNotes("");
      setPdfFile(null);
      setPoNumber("");
      setPoDate("");
      setPoPdfFile(null);
      setPoParseResult(null);
      setPoNumError("");
      loadOrders();
      onBillCreated(data.bill);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create bill");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  /* ── PDF parser ─────────────────────────────────────────────────── */
  const parsePdf = async (file) => {
    setParsing(true);
    setParseResult(null);
    try {
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer })
        .promise;

      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map((item) => item.str).join(" ") + "\n";
      }

      let invoiceNo = null;
      const invoicePatterns = [
        /(?:Invoice\s*No\.?|Invoice\s*Number|Bill\s*No\.?)\s*[:\-]?\s*([A-Z0-9\/\-]+)/i,
        /([A-Z]{2,}[\/\-]\w+[\/\-]\w+[\/\-]\w+)/,
        /([A-Z]{2,}-\d{4}-\d+)/,
      ];
      for (const pat of invoicePatterns) {
        const m = fullText.match(pat);
        if (m) {
          invoiceNo = m[1].trim();
          break;
        }
      }

      let invoiceDate = null;
      const datePatterns = [
        /(?:Dated?|Invoice\s*Date|Bill\s*Date)\s*[:\-]?\s*(\d{1,2}[-\/]\w+[-\/]\d{2,4})/i,
        /(\d{1,2}[-\/](Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*[-\/]\d{2,4})/i,
        /(\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4})/,
      ];
      for (const pat of datePatterns) {
        const m = fullText.match(pat);
        if (m) {
          const raw = m[1] || m[0];
          const parsed = new Date(raw.replace(/-/g, " "));
          if (!isNaN(parsed)) {
            invoiceDate = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          } else {
            const parts = raw.split(/[-\/]/);
            if (parts.length === 3) {
              const year = parts[2].length === 2 ? "20" + parts[2] : parts[2];
              const d = new Date(`${parts[1]} ${parts[0]} ${year}`);
invoiceDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
          }
          if (invoiceDate && invoiceDate !== "Invalid Date") break;
          invoiceDate = null;
        }
      }

      let invoiceTotal = null;
      const totalPatterns = [
        /(?:Grand\s*Total|Total\s*Amount|Amount\s*Chargeable|Net\s*Total|Total)[^₹\d]*[₹Rs\.]*\s*([\d,]+\.?\d*)/i,
        /[₹₨]\s*([\d,]+\.?\d+)/g,
      ];
      const labelMatch = fullText.match(totalPatterns[0]);
      if (labelMatch)
        invoiceTotal = parseFloat(labelMatch[1].replace(/,/g, ""));
      if (!invoiceTotal) {
        const allAmounts = [...fullText.matchAll(/[₹₨]\s*([\d,]+\.?\d+)/g)]
          .map((m) => parseFloat(m[1].replace(/,/g, "")))
          .filter((n) => !isNaN(n));
        if (allAmounts.length) invoiceTotal = Math.max(...allAmounts);
      }

      // ── GST extraction (Buyer / Bill To section) ────────────────────
      // Indian invoices have both seller GST and buyer GST.
      // Step 1: isolate the Buyer / Bill To block, look for GST inside it.
      // Step 2 fallback: if no labelled buyer section, take the last GST in
      //   the doc (buyer details conventionally follow seller on Indian invoices).
      const gstPattern = /[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]/i;

      let pdfCustomerGst = null;

      // Try to find a "Buyer / Bill To" labelled section and extract GST from it
      const buyerSectionMatch = fullText.match(
        /(?:Buyer|Bill\s*To|Billed\s*To|Consignee|Ship\s*To)[^\n]{0,40}[\n]((?:.|[\n]){0,800}?)(?=Seller|Supplier|Consignor|\bDescription\b|\bSr\.?\s*No\b|\bItem\b|\bHSN\b|GSTIN\s*of\s*Supplier|$)/i,
      );

      if (buyerSectionMatch) {
        const buyerBlock = buyerSectionMatch[1];
        const m = buyerBlock.match(new RegExp(gstPattern.source, "i"));
        if (m) pdfCustomerGst = m[0].toUpperCase().trim();
      }

      // Fallback: multiple GSTs found but no labelled section — take the last one
      if (!pdfCustomerGst) {
        const allGsts = [
          ...fullText.matchAll(new RegExp(gstPattern.source, "gi")),
        ];
        if (allGsts.length > 1) {
          pdfCustomerGst = allGsts[allGsts.length - 1][0].toUpperCase().trim();
        }
        // Single GST = only seller is GST-registered; buyer has none in PDF
      }

      const customerGst = (pickedOrders[0]?.gstNo || "").toUpperCase().trim();
      const gstMatched = !!(
        pdfCustomerGst &&
        customerGst &&
        pdfCustomerGst === customerGst
      );
      const gstMismatch = !!(
        pdfCustomerGst &&
        customerGst &&
        pdfCustomerGst !== customerGst
      );
      // ─────────────────────────────────────────────────────────────

      const billGrand = totals.grand;
      const matched =
        invoiceTotal !== null && Math.abs(invoiceTotal - billGrand) === 0;

      const result = {
        invoiceNo,
        invoiceDate,
        invoiceTotal,
        matched,
        pdfCustomerGst,
        customerGst,
        gstMatched,
        gstMismatch,
        raw: fullText.slice(0, 500),
      };
      setParseResult(result);

      if (invoiceNo) setBillNumber(invoiceNo);
      if (invoiceDate) setBillDate(invoiceDate);

      if (gstMismatch) {
        toast.error(
          `GST mismatch: PDF has ${pdfCustomerGst} but customer on file has ${customerGst}`,
          { duration: 7000 },
        );
      }
      if (!matched && invoiceTotal !== null) {
        toast.error(
          `Total mismatch: PDF shows ₹${invoiceTotal.toLocaleString("en-IN")} but bill is ₹${billGrand.toLocaleString("en-IN")}`,
          { duration: 6000 },
        );
      } else if (matched) {
        toast.success("PDF parsed — invoice details filled in");
      } else {
        toast("PDF attached. Could not extract total to verify.", {
          icon: "ℹ️",
        });
      }
    } catch (err) {
      console.error("PDF parse error:", err);
      toast.error("Could not parse PDF — please fill details manually");
      setParseResult(null);
    } finally {
      setParsing(false);
    }
  };

  const checkBillNumber = async (val) => {
    if (!val.trim()) return;
    setBillNumError("");
    setChecking(true);
    try {
      const { data } = await API.get(
        `/bills/check-number?billNumber=${encodeURIComponent(val.trim())}`,
      );
      if (data.exists) {
        const b = data.bill;
        setBillNumError(
          `Bill number "${val.trim()}" already exists` +
            (b
              ? ` — ${b.companyName || b.customerName}, ${fmt(b.billDate)}`
              : "") +
            ".",
        );
      } else {
        setBillNumError("");
      }
    } catch {
      // silently ignore
    } finally {
      setChecking(false);
    }
  };

  /* ── PO number duplicate check ─────────────────────────────────── */
  const checkPoNumber = async (val) => {
    if (!val.trim()) { setPoNumError(""); return; }
    setPoNumError("");
    setPoChecking(true);
    try {
      const ref = pickedOrders[0];
      const params = new URLSearchParams({ poNumber: val.trim() });
      if (billOrders.length) params.set("orderIds", billOrders.map((o) => o.id).join(","));
      if (ref?.businessId) params.set("businessId", ref.businessId);
      else if (ref?.customerName) params.set("customerName", ref.customerName);
      const { data } = await API.get(`/bills/check-po-number?${params}`);
      if (data.exists) {
        setPoNumError(
          `PO number "${val.trim()}" is already used on bill ${data.bill?.billNumber || ""} for this customer.`
        );
      } else {
        setPoNumError("");
      }
    } catch {
      // silently ignore
    } finally {
      setPoChecking(false);
    }
  };

  /* ── PO PDF parser ─────────────────────────────────────────────── */
  const parsePoPdf = async (file) => {
    setPoParsing(true);
    setPoParseResult(null);
    try {
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map((item) => item.str).join(" ") + "\n";
      }

      // Extract PO Number
      let extractedPoNo = null;
      const poPatterns = [
        /(?:Purchase\s*Order\s*(?:No|Number|#)|P\.?O\.?\s*(?:No|Number|#)|PO\s*(?:No|Number|#))\s*[:\-]?\s*([A-Z0-9\/_\-]+)/i,
        /(?:Order\s*(?:No|Number|#))\s*[:\-]?\s*([A-Z0-9\/_\-]{4,30})/i,
      ];
      for (const pat of poPatterns) {
        const m = fullText.match(pat);
        if (m) { extractedPoNo = m[1].trim(); break; }
      }

      // Extract PO Date
      let extractedPoDate = null;
      const datePatterns = [
        /(?:Date|PO\s*Date|Order\s*Date|Dated?)\s*[:\-]?\s*(\d{1,2}[-\/]\w+[-\/]\d{2,4})/i,
        /(\d{1,2}[-\/](Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*[-\/]\d{2,4})/i,
        /(\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4})/,
      ];
      for (const pat of datePatterns) {
        const m = fullText.match(pat);
        if (m) {
          const raw = m[1] || m[0];
          const parsed = new Date(raw.replace(/-/g, " "));
          if (!isNaN(parsed)) {
            extractedPoDate = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
          } else {
            const parts = raw.split(/[-\/]/);
            if (parts.length === 3) {
              const year = parts[2].length === 2 ? "20" + parts[2] : parts[2];
              const d = new Date(`${parts[1]} ${parts[0]} ${year}`);
              if (!isNaN(d)) extractedPoDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            }
          }
          if (extractedPoDate && extractedPoDate !== "NaN-NaN-NaN") break;
          extractedPoDate = null;
        }
      }

      const result = { poNo: extractedPoNo, poDate: extractedPoDate };
      setPoParseResult(result);

      if (extractedPoNo) { setPoNumber(extractedPoNo); checkPoNumber(extractedPoNo); }
      if (extractedPoDate) setPoDate(extractedPoDate);

      if (extractedPoNo || extractedPoDate) {
        toast.success("PO PDF parsed — fields auto-filled");
      } else {
        toast("PO PDF attached. Could not extract PO number/date — fill manually.", { icon: "ℹ️" });
      }
    } catch (err) {
      console.error("PO PDF parse error:", err);
      toast.error("Could not parse PO PDF — please fill details manually");
      setPoParseResult(null);
    } finally {
      setPoParsing(false);
    }
  };

  /* ── Step 1: Order picker ── */
  if (step === 1)
    return (
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {selectedOrders.size > 0 && (
            <button
              onClick={() => {
                if (!sameCustomer) {
                  toast.error(
                    "All selected orders must belong to the same customer",
                  );
                  return;
                }
                setStep(2);
              }}
              style={{
                padding: "8px 18px",
                borderRadius: 8,
                background: "var(--primary)",
                color: "#fff",
                border: "none",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Prepare Bill ({selectedOrders.size} order
              {selectedOrders.size > 1 ? "s" : ""}) →
            </button>
          )}
        </div>

        {!sameCustomer && selectedOrders.size > 1 && (
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 12,
              fontSize: 13,
              color: "#92400e",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <FiAlertCircle size={15} /> All selected orders must belong to the
            same customer.
          </div>
        )}

        {/* {!loadingOrders && hiddenNoTax > 0 && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 12,
              fontSize: 13,
              color: "#1e40af",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <FiAlertCircle size={15} /> {hiddenNoTax} unbilled order
            {hiddenNoTax > 1 ? "s are" : " is"} not shown because none of{" "}
            {hiddenNoTax > 1 ? "their" : "its"} items have GST/tax above 0%.
          </div>
        )} */}

        {loadingOrders ? (
          <div className="so-empty">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="so-empty">
            {selectedCustomer
              ? `No unbilled orders for ${selectedCustomer.name}.`
              : "No unbilled confirmed orders found."}
          </div>
        ) : (
          <div className="so-card so-card--table">
            <div className="so-table-scroll">
              <table className="so-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const isChecked = selectedOrders.has(o.id);
                    const isExpanded = isChecked; // selecting an order expands its items
                    const items = Array.isArray(o.items) ? o.items : [];
                    return (
                      <React.Fragment key={o.id}>
                        <tr
                          style={{
                            background: isChecked ? "#eff6ff" : undefined,
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            if (e.target.closest("button")) return;
                            toggleOrder(o.id);
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleOrder(o.id)}
                              style={{
                                width: 16,
                                height: 16,
                                cursor: "pointer",
                                pointerEvents: "none",
                              }}
                            />
                          </td>
                          <td>
                            <div className="so-strong">
                              {o.orderNumber}
                              {o.billingStatus === "partial" && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    padding: "1px 7px",
                                    borderRadius: 99,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    background: "#fef3c7",
                                    color: "#92400e",
                                  }}
                                >
                                  Partially billed
                                </span>
                              )}
                            </div>
                            <div className="so-muted">{fmt(o.orderDate)}</div>
                          </td>
                          <td>
                            <div className="so-strong">
                              {o.companyName || o.customerName}
                            </div>
                            <div className="so-muted">
                              {o.companyName ? o.customerName : o.customerPhone}
                            </div>
                          </td>
                          <td className="so-muted">{fmt(o.orderDate)}</td>
                          <td>
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: 99,
                                fontSize: 11,
                                fontWeight: 700,
                                background:
                                  o.status === "delivered"
                                    ? "#dcfce7"
                                    : o.status === "confirmed"
                                      ? "#dbeafe"
                                      : "#f3f4f6",
                                color:
                                  o.status === "delivered"
                                    ? "#16a34a"
                                    : o.status === "confirmed"
                                      ? "#1d4ed8"
                                      : "#6b7280",
                                textTransform: "capitalize",
                              }}
                            >
                              {o.status}
                            </span>
                          </td>
                          <td className="so-muted">
                            {items.length} item{items.length !== 1 ? "s" : ""}
                          </td>
                          <td className="so-strong">{money(o.grandTotal)}</td>
                        </tr>
                        {isExpanded &&
                          items.map((item, itemIdx) => {
                            const imap = selectedItemMap[o.id];
                            const billedOn =
                              billedLines[o.id] &&
                              billedLines[o.id][lineKey(item, itemIdx)];
                            const itemChecked =
                              !billedOn && (!imap || imap.has(item.id));
                            return (
                              <tr
                                key={item.id}
                                style={{
                                  background: "#f8faff",
                                  borderLeft: "3px solid #bfdbfe",
                                }}
                              >
                                <td></td>
                                <td
                                  colSpan={2}
                                  style={{ paddingLeft: 28, fontSize: 13 }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked ? itemChecked : false}
                                    disabled={!isChecked || !!billedOn}
                                    onChange={() => {
                                      if (!isChecked || billedOn) return;
                                      if (!selectedItemMap[o.id]) {
                                        // first manual change: start from "all UNBILLED lines ticked"
                                        const allIds = new Set(
                                          items
                                            .filter(
                                              (i, ix) =>
                                                !(
                                                  billedLines[o.id] &&
                                                  billedLines[o.id][lineKey(i, ix)]
                                                ),
                                            )
                                            .map((i) => i.id),
                                        );
                                        allIds.delete(item.id); // this click un-ticks the clicked line
                                        setItemMap((p) => ({
                                          ...p,
                                          [o.id]: allIds,
                                        }));
                                      } else {
                                        toggleItem(o.id, item.id);
                                      }
                                    }}
                                    style={{
                                      marginRight: 8,
                                      cursor: isChecked
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                  />
                                  {item.product}
                                  {billedOn && (
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#92400e",
                                      }}
                                    >
                                      (billed in {billedOn})
                                    </span>
                                  )}
                                </td>
                                <td style={{ fontSize: 13, color: "#6b7280" }}>
                                  {item.qty} {item.unit}
                                </td>
                                <td style={{ fontSize: 13, color: "#6b7280" }}>
                                  {money(item.price)}
                                </td>
                                <td></td>
                                <td style={{ fontSize: 13, fontWeight: 600 }}>
                                  {money(item.total)}
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );

  /* ── Step 2: Preview + Confirm ── */
  return (
    <div>
      <button
        onClick={() => setStep(1)}
        style={{
          background: "none",
          border: "none",
          color: "var(--primary)",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        ← Back to order selection
      </button>

      <div
        style={{
          background: "#f8faff",
          border: "1px solid #bfdbfe",
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#3b82f6",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          Linked Orders ({billOrders.length}) · {billLines.length} line
          {billLines.length !== 1 ? "s" : ""}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {billOrders.map((o) => (
            <span
              key={o.id}
              style={{
                padding: "3px 10px",
                borderRadius: 99,
                background: "#dbeafe",
                color: "#1d4ed8",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {o.orderNumber}
            </span>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>
          <strong>Customer:</strong>{" "}
          {pickedOrders[0]?.companyName || pickedOrders[0]?.customerName}
          {pickedOrders[0]?.gstNo && (
            <span style={{ marginLeft: 12, color: "#6b7280" }}>
              GST: {pickedOrders[0].gstNo}
            </span>
          )}
        </div>
      </div>

      <div className="so-card so-card--table" style={{ marginBottom: 20 }}>
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #f3f4f6",
            fontSize: 13,
            fontWeight: 700,
            color: "#374151",
          }}
        >
          Consolidated Items
          <span
            style={{
              marginLeft: 8,
              fontWeight: 400,
              color: "#6b7280",
              fontSize: 12,
            }}
          >
            (merged by product name + unit price)
          </span>
        </div>
        <div className="so-table-scroll">
          <table className="so-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Unit Price</th>
                <th>Total Qty</th>
                <th>Tax %</th>
                <th>Line Total</th>
                <th>From Orders</th>
              </tr>
            </thead>
            <tbody>
              {consolidated.map((item, i) => (
                <tr key={i}>
                  <td className="so-strong">{item.product}</td>
                  <td>{money(item.unitPrice)}</td>
                  <td>
                    {item.totalQty}{" "}
                    {item.unit && <span className="so-muted">{item.unit}</span>}
                  </td>
                  <td className="so-muted">{item.taxPercent}%</td>
                  <td className="so-strong">{money(item.lineTotal)}</td>
                  <td className="so-muted" style={{ fontSize: 11 }}>
                    {item.sourceOrders
                      .map((s) => `${s.orderNumber}(×${s.qty})`)
                      .join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            gap: 24,
            fontSize: 13,
            borderTop: "1px solid #f3f4f6",
            justifyContent: "flex-end",
          }}
        >
          <span style={{ color: "#374151" }}>
            Taxable: <strong>{money(totals.taxable)}</strong>
          </span>
          <span style={{ color: "#6b7280" }}>
            Tax: <strong>{money(totals.tax)}</strong>
          </span>
          {totals.roundOff !== 0 && (
            <span style={{ color: "#6b7280" }}>
              Round Off: <strong>{totals.roundOff > 0 ? '+' : ''}{money(totals.roundOff)}</strong>
            </span>
          )}
          <span style={{ color: "#111", fontSize: 15 }}>
            Grand Total: <strong>{money(totals.grand)}</strong>
          </span>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 20,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#374151",
            marginBottom: 14,
          }}
        >
          Bill Details
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          <div>
            <label style={labelStyle}>Bill Number *</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={billNumber}
                onChange={(e) => {
                  setBillNumber(e.target.value);
                  setBillNumError("");
                }}
                onBlur={(e) => checkBillNumber(e.target.value)}
                placeholder="e.g. INV-2025-041"
                style={{
                  ...inputStyle,
                  borderColor: billNumError ? "#dc2626" : "#e5e7eb",
                  paddingRight: billNumChecking ? 32 : undefined,
                }}
              />
              {billNumChecking && (
                <span
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    border: "2px solid #3b82f6",
                    borderTopColor: "transparent",
                    display: "inline-block",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
              )}
            </div>
            {billNumError && (
              <div
                style={{
                  marginTop: 5,
                  fontSize: 12,
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 5,
                }}
              >
                <span>⚠️</span> {billNumError}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Bill Date</label>
            <input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              style={{ ...inputStyle, cursor: 'pointer' }}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>
              Upload Bill PDF
              <span
                style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}
              >
                — auto-fills invoice number &amp; date, verifies total
              </span>
            </label>
            <div
              onClick={() => !parsing && pdfRef.current?.click()}
              style={{
                border: `2px dashed ${pdfFile ? "#3b82f6" : "#d1d5db"}`,
                borderRadius: 8,
                padding: "14px 16px",
                cursor: parsing ? "wait" : "pointer",
                background: pdfFile ? "#eff6ff" : "#fafafa",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "all 0.2s",
              }}
            >
              {parsing ? (
                <span
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid #3b82f6",
                      borderTopColor: "transparent",
                      display: "inline-block",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Parsing PDF…
                </span>
              ) : (
                <>
                  <FiPaperclip
                    size={16}
                    style={{
                      color: pdfFile ? "#3b82f6" : "#9ca3af",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      color: pdfFile ? "#1d4ed8" : "#6b7280",
                    }}
                  >
                    {pdfFile ? pdfFile.name : "Click to attach PDF (max 10 MB)"}
                  </span>
                  {pdfFile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPdfFile(null);
                        setParseResult(null);
                        setBillNumber("");
                        setBillDate(new Date().toISOString().slice(0, 10));
                      }}
                      style={{
                        marginLeft: "auto",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#6b7280",
                      }}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
            <input
              ref={pdfRef}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0] || null;
                setPdfFile(file);
                setParseResult(null);
                if (file) parsePdf(file);
              }}
            />
            {parseResult && !parsing && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  flexWrap: "wrap",
                  background: parseResult.matched
                    ? "#f0fdf4"
                    : parseResult.invoiceTotal === null
                      ? "#f8faff"
                      : "#fef9c3",
                  border: `1px solid ${parseResult.matched ? "#bbf7d0" : parseResult.invoiceTotal === null ? "#bfdbfe" : "#fde68a"}`,
                }}
              >
                <span style={{ fontSize: 16 }}>
                  {parseResult.matched
                    ? "✅"
                    : parseResult.invoiceTotal === null
                      ? "ℹ️"
                      : "⚠️"}
                </span>
                <div style={{ flex: 1 }}>
                  {parseResult.invoiceNo && (
                    <div style={{ color: "#374151" }}>
                      Invoice No: <strong>{parseResult.invoiceNo}</strong>
                      {billNumber === parseResult.invoiceNo && (
                        <span style={{ color: "#16a34a", marginLeft: 6 }}>
                          ↑ auto-filled
                        </span>
                      )}
                    </div>
                  )}
                  {parseResult.invoiceDate && (
                    <div style={{ color: "#374151" }}>
                      Date:{" "}
                      <strong>
                        {new Date(parseResult.invoiceDate).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </strong>
                      <span style={{ color: "#16a34a", marginLeft: 6 }}>
                        ↑ auto-filled
                      </span>
                    </div>
                  )}
                  {/* ── GST match row ── */}
                  <div style={{ marginTop: 4 }}>
                    {parseResult.pdfCustomerGst ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: "#374151" }}>
                          Customer GST in PDF:{" "}
                          <strong style={{ letterSpacing: "0.03em" }}>
                            {parseResult.pdfCustomerGst}
                          </strong>
                        </span>
                        {parseResult.gstMatched && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#16a34a",
                              background: "#dcfce7",
                              borderRadius: 999,
                              padding: "2px 8px",
                            }}
                          >
                            ✓ matches customer GST on file
                          </span>
                        )}
                        {parseResult.gstMismatch && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#dc2626",
                              background: "#fee2e2",
                              borderRadius: 999,
                              padding: "2px 8px",
                            }}
                          >
                            ✗ mismatch — customer on file has{" "}
                            {parseResult.customerGst || "no GST"}
                          </span>
                        )}
                        {!parseResult.gstMatched &&
                          !parseResult.gstMismatch && (
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>
                              (no GST on our data for this customer to compare)
                            </span>
                          )}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#9ca3af" }}>
                        No customer GST on invoice
                      </div>
                    )}
                  </div>
                  {parseResult.invoiceTotal !== null ? (
                    <div
                      style={{
                        color: parseResult.matched ? "#16a34a" : "#92400e",
                        fontWeight: 600,
                        marginTop: 2,
                      }}
                    >
                      PDF Total: ₹
                      {Number(parseResult.invoiceTotal).toLocaleString(
                        "en-IN",
                        { minimumFractionDigits: 2 },
                      )}
                      {parseResult.matched
                        ? " ✓ matches bill total"
                        : ` ≠ Bill Total ₹${Number(totals.grand).toLocaleString("en-IN", { minimumFractionDigits: 2 })} — please verify`}
                    </div>
                  ) : (
                    <div style={{ color: "#6b7280" }}>
                      Could not extract total — verify manually
                    </div>
                  )}
                  {parseResult.invoiceTotal !== null &&
                    !parseResult.matched && (
                      <div
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: "1px dashed #fcd34d",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#92400e",
                            marginBottom: 6,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Adjust Bill Total to match PDF
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              color: "#374151",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Override Grand Total:
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            defaultValue={parseResult.invoiceTotal}
                            onBlur={(e) => {
                              const val = Number(e.target.value);
                              if (val > 0) {
                                setGrandTotalOverride(val);
                                toast.success(
                                  `Bill total set to ₹${val.toLocaleString("en-IN")}`,
                                );
                              }
                            }}
                            style={{
                              width: 140,
                              padding: "6px 10px",
                              borderRadius: 7,
                              border: "1px solid #fcd34d",
                              background: "#fff",
                              fontSize: 13,
                              fontWeight: 600,
                              outline: "none",
                            }}
                          />
                          {grandTotalOverride && (
                            <button
                              onClick={() => setGrandTotalOverride(null)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "#9ca3af",
                                fontSize: 12,
                              }}
                            >
                              Reset
                            </button>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#92400e",
                            marginTop: 5,
                          }}
                        >
                          ⚠️ This overrides the bill grand total only —
                          individual order amounts are unchanged.
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
          {/* ── Purchase Order Section ── */}
          <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #f3f4f6", paddingTop: 16, marginTop: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
              Purchase Order (optional)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              {/* PO Number */}
              <div>
                <label style={labelStyle}>PO Number</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    value={poNumber}
                    onChange={(e) => { setPoNumber(e.target.value); setPoNumError(""); }}
                    onBlur={(e) => checkPoNumber(e.target.value)}
                    placeholder="e.g. PO-2026-001"
                    style={{ ...inputStyle, borderColor: poNumError ? "#dc2626" : "#e5e7eb", paddingRight: poNumChecking ? 32 : undefined }}
                  />
                  {poNumChecking && (
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, borderRadius: "50%", border: "2px solid #3b82f6", borderTopColor: "transparent", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  )}
                </div>
                {poNumError && (
                  <div style={{ marginTop: 5, fontSize: 12, color: "#dc2626", display: "flex", alignItems: "flex-start", gap: 5 }}>
                    <span>⚠️</span> {poNumError}
                  </div>
                )}
              </div>

              {/* PO Date */}
              <div>
                <label style={labelStyle}>PO Date</label>
                <input
                  type="date"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                />
              </div>

              {/* PO PDF Upload */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>
                  Upload PO PDF
                  <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}>
                    — auto-fills PO number &amp; date
                  </span>
                </label>
                <div
                  onClick={() => !poParsing && poPdfRef.current?.click()}
                  style={{
                    border: `2px dashed ${poPdfFile ? "#8b5cf6" : "#d1d5db"}`,
                    borderRadius: 8, padding: "14px 16px",
                    cursor: poParsing ? "wait" : "pointer",
                    background: poPdfFile ? "#f5f3ff" : "#fafafa",
                    display: "flex", alignItems: "center", gap: 10, transition: "all 0.2s",
                  }}
                >
                  {poParsing ? (
                    <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #8b5cf6", borderTopColor: "transparent", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                      Parsing PO PDF…
                    </span>
                  ) : (
                    <>
                      <FiPaperclip size={16} style={{ color: poPdfFile ? "#8b5cf6" : "#9ca3af", flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: poPdfFile ? "#6d28d9" : "#6b7280" }}>
                        {poPdfFile ? poPdfFile.name : "Click to attach PO PDF (max 10 MB)"}
                      </span>
                      {poPdfFile && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setPoPdfFile(null); setPoParseResult(null); setPoNumber(""); setPoDate(""); }}
                          style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <input
                  ref={poPdfRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0] || null;
                    setPoPdfFile(file);
                    setPoParseResult(null);
                    if (file) parsePoPdf(file);
                  }}
                />
                {poParseResult && !poParsing && (
                  <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <span>📄</span>
                    <div>
                      {poParseResult.poNo && (
                        <div style={{ color: "#374151" }}>PO No: <strong>{poParseResult.poNo}</strong> <span style={{ color: "#16a34a" }}>↑ auto-filled</span></div>
                      )}
                      {poParseResult.poDate && (
                        <div style={{ color: "#374151" }}>
                          PO Date: <strong>{new Date(poParseResult.poDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</strong> <span style={{ color: "#16a34a" }}>↑ auto-filled</span>
                        </div>
                      )}
                      {!poParseResult.poNo && !poParseResult.poDate && (
                        <div style={{ color: "#6b7280" }}>Could not extract PO details — fill manually</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes for this bill…"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 20,
          }}
        >
          <button
            onClick={() => setStep(1)}
            style={{
              padding: "9px 20px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Back
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !!billNumError || billNumChecking || !!poNumError || poNumChecking}
            style={{
              padding: "9px 22px",
              borderRadius: 8,
              border: "none",
              background: "var(--primary)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            <FiCheckCircle size={14} style={{ marginRight: 6 }} />
            {uploading
              ? "Uploading PDF…"
              : saving
                ? "Creating…"
                : "Create Bill"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── All Bills Section ───────────────────────────────────────────── */
const AllBillsSection = ({ refreshTrigger, selectedCustomer }) => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logModal, setLogModal] = useState(null);
  const [historyModal, setHistory] = useState(null);
  const [showMatch, setShowMatch] = useState(false);   // Excel match modal
  const [verifiedFilter, setVerifiedFilter] = useState("");  // "", "yes", "no"
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 25;

  const load = useCallback(
    async ({ targetPage = 1 } = {}) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: targetPage,
          limit: PAGE_SIZE,
        });
        // Server filters by customer first, then paginates only matching bills
        if (selectedCustomer?.businessId) {
          params.set("businessId", selectedCustomer.businessId);
        } else if (selectedCustomer) {
          if (selectedCustomer.phone)
            params.set("customerPhone", selectedCustomer.phone);
          if (selectedCustomer.name)
            params.set("customerName", selectedCustomer.name);
        }
        if (verifiedFilter) params.set("verified", verifiedFilter);
        const { data } = await API.get(`/bills?${params}`);
        const result = data.bills || [];
        setBills(result);
        setTotalPages(data.pages || 1);
        setTotalCount(data.count || 0);
        setPage(targetPage);
      } catch {
        toast.error("Failed to load bills");
      } finally {
        setLoading(false);
      }
    },
    [selectedCustomer, verifiedFilter],
  ); // eslint-disable-line

  useEffect(() => {
    load();
  }, [refreshTrigger, selectedCustomer, verifiedFilter]); // eslint-disable-line

  const updateBill = (updated) =>
    setBills((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));

  return (
    <div>
      {/* Excel match toolbar */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <select
          value={verifiedFilter}
          onChange={(e) => setVerifiedFilter(e.target.value)}
          className="so-btn"
          style={{ cursor: "pointer" }}
          title="Filter by Excel-match verification"
        >
          <option value="">All bills</option>
          <option value="yes">Verified only</option>
          <option value="no">Not verified</option>
        </select>
        <button className="so-btn so-btn--primary" onClick={() => setShowMatch(true)}>
          <FiCheckCircle size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
          Match with Excel
        </button>
      </div>

      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : bills.length === 0 ? (
          <div className="so-empty">
            {selectedCustomer
              ? `No bills found for ${selectedCustomer.name}.`
              : "No bills yet. Generate one above."}
          </div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Customer</th>
                  <th>Orders</th>
                  <th>Bill Date</th>
                  <th>Grand Total</th>
                  <th>Settled</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => {
                  const sm = BILL_STATUS[b.status] || BILL_STATUS.generated;
                  const isCancelled = b.status === "cancelled";
                  const balance = isCancelled
                    ? 0
                    : Math.max(
                        0,
                        Number(b.grandTotal) - Number(b.amountReceived),
                      );
                  const entries = Array.isArray(b.paymentEntries)
                    ? b.paymentEntries
                    : [];
                  const orderCount = Array.isArray(b.linkedOrderIds)
                    ? b.linkedOrderIds.length
                    : 0;
                  return (
                    <tr
                      key={b.id}
                      style={{
                        cursor: "pointer",
                        opacity: isCancelled ? 0.6 : 1,
                      }}
                      onClick={(e) => {
                        if (e.target.closest("button") || e.target.closest("a"))
                          return;
                        navigate(`/admin/billing/${b.id}`);
                      }}
                    >
                      <td>
                        <div
                          className="so-strong"
                          style={isCancelled ? { textDecoration: "line-through" } : undefined}
                        >
                          {b.billNumber}
                        </div>
                        {isCancelled && b.originalBillNumber && (
                          <div className="so-muted" style={{ fontSize: 11 }}>
                            Was {b.originalBillNumber}
                          </div>
                        )}
                        {b.pdfUrl && (
                          <a
                            href={resolvePdfUrl(b.pdfUrl)}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              fontSize: 11,
                              color: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              marginTop: 2,
                            }}
                          >
                            <FiFileText size={11} /> INVOICE
                          </a>
                        )}
                      </td>
                      <td>
                        <div className="so-strong">
                          {b.companyName || b.customerName}
                        </div>
                        <div className="so-muted">
                          {b.companyName ? b.customerName : b.customerPhone}
                        </div>
                      </td>
                      <td className="so-muted">
                        {orderCount} order{orderCount !== 1 ? "s" : ""}
                      </td>
                      <td className="so-muted">{fmt(b.billDate)}</td>
                      <td className="so-strong">{money(b.grandTotal)}</td>
                      <td style={{ color: "#16a34a", fontWeight: 600 }}>
                        {money(b.amountReceived)}
                        {billSettlement(b).tds > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>
                            {money(billSettlement(b).paid)} paid +{" "}
                            <span style={{ color: "#b45309" }}>{money(billSettlement(b).tds)} TDS</span>
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          color: balance > 0 ? "#dc2626" : "#6b7280",
                          fontWeight: balance > 0 ? 600 : 400,
                        }}
                      >
                        {balance > 0 ? money(balance) : "—"}
                      </td>
                      <td>
                        <span
                          className="so-badge"
                          style={{ background: sm.bg, color: sm.color }}
                        >
                          {sm.label}
                        </span>
                        {b.isVerified && !isCancelled && (
                          <div
                            title={`Verified${b.verifySource ? " · " + b.verifySource : ""}${b.verifiedAt ? " · " + new Date(b.verifiedAt).toLocaleDateString("en-IN") : ""}`}
                            style={{ fontSize: 11, color: "#15803d", fontWeight: 600, marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}
                          >
                            <FiCheckCircle size={11} /> Verified
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          {b.status !== "paid" && !isCancelled && (
                            <button
                              onClick={() => setLogModal(b)}
                              title="Record Payment"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "5px 10px",
                                borderRadius: 7,
                                border: "1px solid var(--primary)",
                                background: "var(--primary)",
                                color: "#fff",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              <FiPlus size={12} /> Log
                            </button>
                          )}
                          {entries.length > 0 && (
                            <button
                              onClick={() => setHistory(b)}
                              title="Payment History"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "5px 10px",
                                borderRadius: 7,
                                border: "1px solid #6b7280",
                                background: "#f3f4f6",
                                color: "#374151",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              <FiList size={12} /> {entries.length}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 16,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            Page {page} of {totalPages} · {totalCount} bills
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page - 1 })}
              disabled={page <= 1}
              style={{ opacity: page <= 1 ? 0.4 : 1 }}
            >
              ‹ Prev
            </button>
            <button
              className="so-btn"
              onClick={() => load({ targetPage: page + 1 })}
              disabled={page >= totalPages}
              style={{ opacity: page >= totalPages ? 0.4 : 1 }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}

      {logModal && (
        <RecordPaymentModal
          bill={logModal}
          onClose={() => setLogModal(null)}
          onSaved={(updated) => {
            updateBill(updated);
            setLogModal(null);
          }}
        />
      )}
      {historyModal && (
        <PaymentHistoryModal
          bill={historyModal}
          onClose={() => setHistory(null)}
        />
      )}
      {showMatch && (
        <ReconcileModal
          type="sales"
          onClose={() => setShowMatch(false)}
          onVerified={() => load({ targetPage: page })}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   CLIENT RECEIPT SECTION
   Multi-bill payment from one client in a single transaction.
   ═══════════════════════════════════════════════════════════════════ */

/* ── Helpers ────────────────────────────────────────────────────── */
const num = (v) => Number(v) || 0;

const PAYMENT_MODES = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi',           label: 'UPI' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'other',         label: 'Other' },
];

/* ────────────────────────────────────────────────────────────────────
   Step 1 — Client picker (business only; receipts require a businessId
   so we can read the credit wallet and open bills)
   ──────────────────────────────────────────────────────────────────── */
const ClientPicker = ({ selected, onSelect }) => {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await API.get('/business', { params: { search: query, limit: 10 } });
setResults(data.businesses || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  if (selected) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1.5px solid #6366f1', borderRadius: 8, background: '#eef2ff' }}>
        <FiBriefcase size={15} color="#6366f1" />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#3730a3', flex: 1 }}>
          {selected.companyName}
          <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
            {selected.contactPersonName || ''}
          </span>
        </span>
        <button onClick={() => onSelect(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
          <FiX size={15} />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8 }}>
        <FiSearch size={14} color="#9ca3af" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search client by company name…"
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, background: 'transparent' }}
        />
        {loading && <span style={{ fontSize: 11, color: '#9ca3af' }}>…</span>}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 999, maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
          {results.map(b => (
            <button key={b.id} onClick={() => { onSelect(b); setQuery(''); setOpen(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <FiBriefcase size={13} color="#6366f1" />
              <span style={{ fontWeight: 600 }}>{b.companyName}</span>
              <span style={{ color: '#9ca3af', fontSize: 12 }}>{b.contactPersonName || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Step 2 — Bill selection table
   Shows all open (unpaid / partial) bills for the chosen client.
   ──────────────────────────────────────────────────────────────────── */
const BillSelectionTable = ({ bills, selected, onToggle, onToggleAll }) => {
  const allSelected = bills.length > 0 && bills.every(b => selected.has(b.id));

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Open Bills — select which to settle
      </div>
      {bills.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 13, border: '1px dashed #e5e7eb', borderRadius: 8 }}>
          No open bills for this client
        </div>
      )}
      {bills.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
                </th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Bill #</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Date</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Total</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Settled</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Balance</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b, idx) => {
                const isSelected = selected.has(b.id);
                const balance    = num(b.grandTotal) - num(b.amountReceived);
                const meta       = BILL_STATUS[b.status] || BILL_STATUS.generated;
                return (
                  <tr key={b.id}
                    onClick={() => onToggle(b.id, balance)}
                    style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', background: isSelected ? '#f0f9ff' : '#fff', transition: 'background 0.1s' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <input type="checkbox" checked={isSelected} onChange={() => onToggle(b.id, balance)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e40af' }}>{b.billNumber}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>{fmt(b.billDate)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600 }}>{money(b.grandTotal)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#16a34a' }}>
                      {money(b.amountReceived)}
                      {billSettlement(b).tds > 0 && (
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{money(billSettlement(b).paid)} paid + <span style={{ color: '#b45309' }}>{money(billSettlement(b).tds)} TDS</span></div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{money(balance)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: meta.bg, color: meta.color }}>{meta.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Step 3 — Amount + allocation
   Admin enters total received; chooses FIFO or manual split.
   Shows live preview table before save.
   ──────────────────────────────────────────────────────────────────── */

const AllocationStep = ({
  selectedBills, totalAmount, setTotalAmount,
  creditBalance, creditUsed, setCreditUsed,
  splitMode, setSplitMode,
  manualAmounts, setManualAmounts,
  bankAccounts, form, setForm,
  tdsInput, setTdsInput, resolved,
}) => {
  const cashAmt    = num(totalAmount);
  const creditAmt  = num(creditUsed);
  const effective  = cashAmt + creditAmt;           // bank/cash + wallet credit
  const tdsAmt     = resolved.amount;               // TDS deducted by the customer
  const toSettle   = effective + tdsAmt;            // what gets knocked off the bills
  const plan       = resolved.plan;
  const perBill    = !!(tdsInput && tdsInput.perBill);   // TDS typed bill by bill
  // The TDS column stays hidden until TDS is in play: a total typed in the yellow box,
  // the "Enter TDS per bill" link, or a value typed straight into a bill's TDS cell.
  const [tdsReveal, setTdsReveal] = useState(false);
  const showTds    = tdsAmt > 0 || perBill || tdsReveal;

  // Typing in a bill's TDS cell switches to per-bill TDS automatically. The other bills
  // keep the TDS they currently have, so nothing jumps.
  const setBillTds = (billId, value) => {
    setTdsReveal(true);
    setTdsInput(p => {
      if (p.perBill) return { ...p, billTds: { ...(p.billTds || {}), [billId]: value } };
      const seed = {};
      plan.rows.forEach(r => { if (r.tds > 0) seed[r.bill.id] = String(r.tds); });
      return { ...p, perBill: true, billTds: { ...seed, [billId]: value } };
    });
  };

  const selectedBalance = selectedBills.reduce((s, b) => s + num(b.grandTotal) - num(b.amountReceived), 0);

  // In manual mode the amount typed per bill is the money RECEIVED for that bill (TDS is separate)
  const manualSum         = selectedBills.reduce((s, b) => s + num(manualAmounts[b.id]), 0);
  const manualSurplus     = effective - manualSum;
  const surplus           = plan.surplus;

  const manualError       = splitMode === 'manual' && Math.abs(manualSurplus) > 0.01
    ? manualSurplus > 0
      ? `${money(manualSurplus)} of the amount received is not given to any bill — enter it against a bill, or it will go to the credit wallet`
      : `Over-allocated by ${money(Math.abs(manualSurplus))} — the Received boxes add up to ${money(manualSum)} but the amount received is ${money(effective)}`
    : null;

  const labelSt = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inputSt = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

      {/* Left — payment details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Details</div>

        {/* Total amount received */}
        <div>
          <label style={labelSt}>Amount Received (₹)</label>
          <input type="number" min="0" step="0.01" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} style={inputSt} placeholder="0.00" />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Money that actually came in (cash / bank / UPI). Leave 0 if you are only recording TDS.</div>
        </div>

        {/* Credit wallet */}
        {creditBalance > 0 && (
          <div style={{ padding: '10px 14px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 700, marginBottom: 6 }}>
              Available Credit Wallet: {money(creditBalance)}
            </div>
            <label style={labelSt}>Apply Credit (₹)</label>
            <input type="number" min="0" step="0.01" max={creditBalance} value={creditUsed}
              onChange={e => setCreditUsed(Math.min(num(e.target.value), creditBalance))}
              style={inputSt} placeholder="0.00" />
          </div>
        )}

        {/* TDS deducted by the customer */}
        <TdsFields input={tdsInput} onChange={setTdsInput} resolved={resolved}
          onShowPerBill={showTds ? undefined : () => setTdsReveal(true)} />

        {/* Mode */}
        <div>
          <label style={labelSt}>Payment Mode</label>
          <select value={form.mode} onChange={e => setForm(p => ({ ...p, mode: e.target.value }))} style={inputSt}>
            {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {/* Bank account — shown when mode is bank_transfer / cheque */}
        {(form.mode === 'bank_transfer' || form.mode === 'cheque') && bankAccounts.length > 0 && (
          <div>
            <label style={labelSt}>Bank Account</label>
            <select value={form.bankAccountId} onChange={e => setForm(p => ({ ...p, bankAccountId: e.target.value }))} style={inputSt}>
              <option value="">— select —</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </div>
        )}

        {/* Date */}
        <div>
          <label style={labelSt}>Payment Date</label>
          <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={inputSt} />
        </div>

        {/* Note */}
        <div>
          <label style={labelSt}>Note (optional)</label>
          <input type="text" value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} style={inputSt} placeholder="Cheque no., reference, etc." />
        </div>

        {/* Summary chip */}
        {toSettle > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {cashAmt > 0 && <span style={{ padding: '4px 10px', borderRadius: 99, background: '#dcfce7', color: '#16a34a', fontSize: 12, fontWeight: 700 }}>Cash: {money(cashAmt)}</span>}
            {creditAmt > 0 && <span style={{ padding: '4px 10px', borderRadius: 99, background: '#ede9fe', color: '#7c3aed', fontSize: 12, fontWeight: 700 }}>Credit: {money(creditAmt)}</span>}
            {tdsAmt > 0 && <span style={{ padding: '4px 10px', borderRadius: 99, background: '#fef3c7', color: '#b45309', fontSize: 12, fontWeight: 700 }}>TDS: {money(tdsAmt)}{resolved.percent ? ` (${fmtPercent(resolved.percent)}%)` : ''}</span>}
            <span style={{ padding: '4px 10px', borderRadius: 99, background: '#dbeafe', color: '#1d4ed8', fontSize: 12, fontWeight: 700 }}>{tdsAmt > 0 ? 'Total settled' : 'Effective'}: {money(toSettle)}</span>
            {surplus > 0 && <span style={{ padding: '4px 10px', borderRadius: 99, background: '#fef3c7', color: '#d97706', fontSize: 12, fontWeight: 700 }}>→ Wallet: +{money(surplus)}</span>}
          </div>
        )}
      </div>

      {/* Right — allocation mode + preview */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allocation Preview</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['fifo', 'manual'].map(m => (
              <button key={m} onClick={() => setSplitMode(m)}
                style={{ padding: '5px 12px', borderRadius: 6, border: `1.5px solid ${splitMode === m ? '#6366f1' : '#e5e7eb'}`, background: splitMode === m ? '#6366f1' : '#fff', color: splitMode === m ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {m === 'fifo' ? 'Auto FIFO' : 'Manual Split'}
              </button>
            ))}
          </div>
        </div>

        {/* Allocation table */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Bill</th>
                <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Balance</th>
                <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Received</th>
                {showTds && <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#b45309' }}>TDS deducted</th>}
                <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {selectedBills.map((b, idx) => {
                const balance = num(b.grandTotal) - num(b.amountReceived);
                const planRow = plan.rows.find(r => r.bill.id === b.id);
                const received = planRow?.cash || 0;            // money received that goes to this bill
                const rowTds = planRow?.tds || 0;
                const typedRowTds = num((tdsInput.billTds || {})[b.id]);
                const rowTdsOver  = !!planRow && planRow.tds > planRow.base + 0.011;
                const remaining = Math.max(0, balance - (planRow?.settle || 0));   // balance − received − TDS

                return (
                  <tr key={b.id} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 600, color: '#1e40af' }}>{b.billNumber}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{money(balance)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                      {splitMode === 'fifo' ? (
                        <span style={{ fontWeight: 700, color: received > 0 ? '#16a34a' : '#9ca3af' }}>{money(received)}</span>
                      ) : (
                        <input type="number" min="0" step="0.01" max={balance}
                          value={manualAmounts[b.id] || ''}
                          onChange={e => {
                            const val = Math.min(num(e.target.value), balance);
                            setManualAmounts(p => ({ ...p, [b.id]: val }));
                          }}
                          style={{ width: 90, padding: '4px 6px', border: `1px solid ${num(manualAmounts[b.id]) > balance ? '#dc2626' : '#d1d5db'}`, borderRadius: 6, fontSize: 13, textAlign: 'right', outline: 'none' }}
                        />
                      )}
                    </td>
                    {showTds && (
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#b45309', fontWeight: 600 }}>
                        <input type="number" min="0" step="0.01" placeholder="0.00"
                          value={perBill ? ((tdsInput.billTds || {})[b.id] ?? '') : (rowTds > 0 ? String(rowTds) : '')}
                          onChange={e => setBillTds(b.id, e.target.value)}
                          style={{ width: 84, padding: '4px 6px', border: `1px solid ${perBill && (rowTdsOver || typedRowTds > rowTds + 0.009) ? '#dc2626' : '#fcd34d'}`, borderRadius: 6, fontSize: 13, textAlign: 'right', outline: 'none', background: '#fffbeb' }}
                        />
                        {perBill && typedRowTds > rowTds + 0.009 && (
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: '#dc2626' }}>max {money(rowTds)}</div>
                        )}
                        {perBill && rowTdsOver && (
                          <div style={{ fontSize: 10.5, fontWeight: 500, color: '#dc2626' }}>more than taxable value</div>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: remaining > 0 ? '#d97706' : '#16a34a', fontWeight: 600 }}>{money(remaining)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb' }}>
                <td colSpan={2} style={{ padding: '9px 12px', fontWeight: 700, fontSize: 13 }}>Total selected balance: {money(selectedBalance)}</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>
                  {money(plan.cashAllocated)}
                </td>
                {showTds && <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{money(tdsAmt)}</td>}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {resolved.overflow > 0.01 ? (
          <div style={{ padding: '10px 14px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 12, lineHeight: 1.5, color: '#92400e' }}>
            <strong>Check before saving: </strong>{overflowMessage(resolved, 'wallet')}
            <div style={{ marginTop: 4 }}>You will be asked to confirm when you save.</div>
          </div>
        ) : surplus > 0 && (
          <div style={{ padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
            <strong>+{money(surplus)}</strong> will be added to this client's credit wallet for future bills.
          </div>
        )}
        {manualError && (
          <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>
            {manualError}
          </div>
        )}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   CreateReceiptSection — the full wizard
   ──────────────────────────────────────────────────────────────────── */
const CreateReceiptSection = ({ onReceiptCreated }) => {
  // Wizard state
  const [step, setStep]               = useState(1); // 1=client, 2=bills, 3=amount
  const [client, setClient]           = useState(null);
  const [openBills, setOpenBills]     = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [creditBalance, setCreditBalance] = useState(0);

  // Opening balance still owed by the selected client (settled via its own receipt)
  const [obSummary, setObSummary]     = useState(null);
  const [showObSettle, setShowObSettle] = useState(false);
  const loadObSummary = useCallback(() => {
    if (!client) { setObSummary(null); return; }
    API.get(`/business/${client.id}/opening-balance`)
      .then(({ data }) => setObSummary(data.openingBalance || null))
      .catch(() => setObSummary(null));
  }, [client]);
  useEffect(() => { loadObSummary(); }, [loadObSummary]);

  // Step 3 state
  // receiptNumber is auto-generated by the server (R00001, R00002…)
  // Fetched as a preview when reaching Step 3. Never typed by admin.
  const [receiptNumber, setReceiptNumber] = useState('');
  const [totalAmount, setTotalAmount]     = useState('');
  const [creditUsed, setCreditUsed]       = useState(0);
  const [splitMode, setSplitMode]         = useState('fifo');
  const [manualAmounts, setManualAmounts] = useState({});
  const [bankAccounts, setBankAccounts]   = useState([]);
  const [form, setForm] = useState({ mode: 'cash', bankAccountId: '', date: new Date().toISOString().slice(0, 10), note: '' });
  const [saving, setSaving] = useState(false);

  // Load open bills when client is selected
  useEffect(() => {
    if (!client) { setOpenBills([]); setCreditBalance(0); setSelectedIds(new Set()); return; }
    setLoadingBills(true);
    Promise.all([
      API.get(`/client-receipts/open-bills/${client.id}`),
      API.get(`/client-receipts/credit/${client.id}`),
    ]).then(([billRes, creditRes]) => {
      setOpenBills(billRes.data.bills || []);
      setCreditBalance(num(creditRes.data.creditBalance));
    }).catch(() => {
      toast.error('Failed to load client bills');
    }).finally(() => setLoadingBills(false));
  }, [client]);

  // Load bank accounts for the payment form
  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  // Auto-fetch next receipt number when reaching Step 3
  useEffect(() => {
    if (step !== 3) return;
    API.get('/client-receipts/next-number')
      .then(({ data }) => setReceiptNumber(data.receiptNumber || ''))
      .catch(() => {});
  }, [step]);

  const selectedBills = openBills.filter(b => selectedIds.has(b.id));

  // TDS (optional) — % and amount are linked; see utils/tds.js
  const [tdsInput, setTdsInput] = useState(EMPTY_TDS);
  const [overflowAsk, setOverflowAsk] = useState(null);   // {overflow, needed} → confirm dialog
  const resolved = resolveTds({
    bills: selectedBills,
    effective: num(totalAmount) + num(creditUsed),
    mode: splitMode === 'manual' ? 'manualCash' : 'fifo',   // manual boxes = money RECEIVED per bill, TDS on top
    manualSettle: manualAmounts,
    input: tdsInput,
  });

  const handleToggleBill = (id, balance) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); setManualAmounts(p => { const n = { ...p }; delete n[id]; return n; }); }
      else { next.add(id); setManualAmounts(p => ({ ...p, [id]: '' })); }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (openBills.every(b => selectedIds.has(b.id))) {
      setSelectedIds(new Set());
      setManualAmounts({});
    } else {
      setSelectedIds(new Set(openBills.map(b => b.id)));
      const m = {};
      openBills.forEach(b => { m[b.id] = ''; });
      setManualAmounts(m);
    }
  };

  const handleSave = async (confirmedOverflow = false) => {
    if (!client) { toast.error('Select a client'); return; }
    if (selectedBills.length === 0) { toast.error('Select at least one bill'); return; }

    const cashAmt   = num(totalAmount);
    const creditAmt = num(creditUsed);
    if (cashAmt === 0 && creditAmt === 0 && resolved.amount <= 0) { toast.error('Enter the amount received or the TDS deducted'); return; }

    // Validate manual: no individual over-allocation (amount typed = cash + TDS settled on the bill)
    if (splitMode === 'manual') {
      for (const r of resolved.plan.rows) {
        if (num(manualAmounts[r.bill.id]) > r.balance + 0.01) {
          toast.error(`${r.bill.billNumber}: allocated amount exceeds balance`);
          return;
        }
      }
    }
    if (resolved.error) { toast.error(resolved.error); return; }

    // payment + TDS is more than the bills need → the extra would go to the wallet: ask first
    if (confirmedOverflow !== true && resolved.amount > 0 && resolved.overflow > 0.01) {
      setOverflowAsk({ overflow: resolved.overflow, needed: resolved.cashNeeded });
      return;
    }
    const allocations = allocationsPayload(resolved.plan);

    try {
      setSaving(true);
      const payload = {
        receiptNumber: receiptNumber.trim(),
        businessId:    client.id,
        customerName:  client.contactPersonName || client.companyName,
customerPhone: client.contactPersonPhone || '',
        companyName:   client.companyName,
        totalAmount:   cashAmt,
        creditUsed:    creditAmt,
        receiptDate:   form.date,
        mode:          form.mode,
        bankAccountId:    form.bankAccountId || null,
        bankAccountLabel: bankAccounts.find(a => String(a.id) === String(form.bankAccountId))?.account_name || null,
        note:          form.note,
        billAllocations: allocations,
        ...tdsPayload(resolved, tdsInput, tdsInput.section),
      };

      const { data } = await API.post('/client-receipts', payload);
      toast.success('Receipt saved successfully');

      if (data.surplusCredit > 0) {
        toast(`+${money(data.surplusCredit)} added to ${client.companyName}'s credit wallet`, { icon: '💰' });
      }

      onReceiptCreated();
      // Reset wizard
      setStep(1); setClient(null); setSelectedIds(new Set());
      setTotalAmount(''); setCreditUsed(0); setReceiptNumber(''); setTdsInput(EMPTY_TDS);
      setForm({ mode: 'cash', bankAccountId: '', date: new Date().toISOString().slice(0, 10), note: '' });
      setManualAmounts({});
      setOverflowAsk(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save receipt');
    } finally {
      setSaving(false);
    }
  };

  const canProceedStep1 = !!client;
  const canProceedStep2 = selectedBills.length > 0;
  const canSave = (num(totalAmount) > 0 || num(creditUsed) > 0 || resolved.amount > 0) && !resolved.error; // receiptNumber is auto-generated

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
        {[
          { n: 1, label: 'Select Client' },
          { n: 2, label: 'Choose Bills' },
          { n: 3, label: 'Enter Payment' },
        ].map((s, idx) => (
          <React.Fragment key={s.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
                background: step > s.n ? '#16a34a' : step === s.n ? '#6366f1' : '#e5e7eb',
                color: step >= s.n ? '#fff' : '#9ca3af',
              }}>
                {step > s.n ? '✓' : s.n}
              </div>
              <span style={{ fontSize: 13, fontWeight: step === s.n ? 700 : 400, color: step === s.n ? '#6366f1' : '#6b7280' }}>{s.label}</span>
            </div>
            {idx < 2 && <div style={{ flex: 1, height: 1, background: '#e5e7eb', margin: '0 12px' }} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Client */}
      {step >= 1 && (
        <div style={{ padding: 20, border: `1.5px solid ${step === 1 ? '#6366f1' : '#e5e7eb'}`, borderRadius: 12, background: step === 1 ? '#fafaff' : '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#374151' }}>1. Select Client</div>
          <ClientPicker selected={client} onSelect={(c) => { setClient(c); if (c) setStep(2); else setStep(1); }} />
          {client && step > 1 && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Change client</button>
            </div>
          )}
        </div>
      )}

      {/* Opening balance pending — settled with its own receipt */}
      {client && obSummary?.canSettle && (
        <div style={{ padding: '14px 18px', border: '1.5px solid #fecaca', borderRadius: 12, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#7f1d1d' }}>
            📒 <strong>{client.companyName}</strong> has an opening balance pending: <strong>{money(obSummary.remaining)}</strong>
            {obSummary.settled > 0 && <span style={{ color: '#6b7280' }}> (of {money(obSummary.amount)}, {money(obSummary.settled)} already settled)</span>}
          </div>
          <button onClick={() => setShowObSettle(true)}
            style={{ padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Settle Opening Balance
          </button>
        </div>
      )}

      {showObSettle && client && obSummary && (
        <SettleOpeningBalanceModal
          business={client}
          summary={obSummary}
          onClose={() => setShowObSettle(false)}
          onSaved={() => { loadObSummary(); onReceiptCreated(); }}
        />
      )}

      {/* Step 2 — Bills */}
      {step >= 2 && client && (
        <div style={{ padding: 20, border: `1.5px solid ${step === 2 ? '#6366f1' : '#e5e7eb'}`, borderRadius: 12, background: step === 2 ? '#fafaff' : '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>2. Choose Bills to Settle</div>
            {creditBalance > 0 && (
              <span style={{ padding: '4px 10px', borderRadius: 99, background: '#ede9fe', color: '#7c3aed', fontSize: 12, fontWeight: 700 }}>
                💰 Credit wallet: {money(creditBalance)}
              </span>
            )}
          </div>
          {loadingBills ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>Loading bills…</div>
          ) : (
            <BillSelectionTable bills={openBills} selected={selectedIds} onToggle={handleToggleBill} onToggleAll={handleToggleAll} />
          )}
          {canProceedStep2 && step === 2 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setStep(3)}
                style={{ padding: '9px 22px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Continue →
              </button>
            </div>
          )}
          {step > 2 && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{selectedBills.length} bill{selectedBills.length !== 1 ? 's' : ''} selected · balance {money(selectedBills.reduce((s, b) => s + num(b.grandTotal) - num(b.amountReceived), 0))}</span>
              <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Change selection</button>
            </div>
          )}
        </div>
      )}

      {/* Step 3 — Amount + allocation */}
      {step >= 3 && selectedBills.length > 0 && (
        <div style={{ padding: 20, border: '1.5px solid #6366f1', borderRadius: 12, background: '#fafaff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>3. Enter Payment & Allocation</div>
          </div>

          {/* Receipt number — auto-generated, read-only */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Receipt Number</label>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
              {receiptNumber || '…'}
              <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>(auto-assigned)</span>
            </div>
          </div>

          <AllocationStep
            selectedBills={selectedBills}
            totalAmount={totalAmount}
            setTotalAmount={setTotalAmount}
            creditBalance={creditBalance}
            creditUsed={creditUsed}
            setCreditUsed={setCreditUsed}
            splitMode={splitMode}
            setSplitMode={setSplitMode}
            manualAmounts={manualAmounts}
            setManualAmounts={setManualAmounts}
            bankAccounts={bankAccounts}
            form={form}
            setForm={setForm}
            tdsInput={tdsInput}
            setTdsInput={setTdsInput}
            resolved={resolved}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button onClick={() => handleSave()} disabled={saving || !canSave}
              style={{ padding: '10px 28px', background: canSave ? '#16a34a' : '#e5e7eb', color: canSave ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: canSave ? 'pointer' : 'not-allowed' }}>
              {saving ? 'Saving…' : 'Save Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* Confirm: payment + TDS is more than the bills need */}
      {overflowAsk && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 460, maxWidth: '94vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#92400e' }}>
              {money(overflowAsk.overflow)} extra — go to the wallet?
            </h3>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              Amount received + TDS is <strong>{money(overflowAsk.overflow)}</strong> more than the selected bills need.
              If you save, this extra will be added to {client?.companyName || "the client"}'s credit wallet and the
              receipt will show <strong>{money(num(totalAmount))}</strong> as money received.
              <div style={{ marginTop: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#92400e' }}>
                If the customer deducted TDS, go back and enter <strong>{money(overflowAsk.needed)}</strong> as the amount received instead.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={() => setOverflowAsk(null)}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                Go back &amp; fix
              </button>
              <button onClick={() => { setOverflowAsk(null); handleSave(true); }}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#d97706', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Yes, save anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   AllReceiptsSection — list view of saved receipts
   ──────────────────────────────────────────────────────────────────── */
const AllReceiptsSection = ({ refreshTrigger }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [q, setQ]               = useState('');
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [reversing, setReversing] = useState(null);
  const [editingId, setEditingId]   = useState(null);
  const { isAdmin } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/client-receipts', { params: { q, page, limit: 25 } });
      setReceipts(data.receipts || []);
      setPages(data.pages || 1);
    } catch { toast.error('Failed to load receipts'); }
    finally { setLoading(false); }
  }, [q, page, refreshTrigger]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleReverse = async (r) => {
    if (!window.confirm(`Reverse receipt ${r.receiptNumber}? This will roll back all bill allocations.`)) return;
    setReversing(r.id);
    try {
      await API.delete(`/client-receipts/${r.id}`);
      toast.success('Receipt reversed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reverse');
    } finally { setReversing(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid #e5e7eb', borderRadius: 8, flex: 1, maxWidth: 320 }}>
          <FiSearch size={13} color="#9ca3af" />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search receipt # or company…"
            style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, background: 'transparent' }} />
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>Loading…</div>}
      {!loading && receipts.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14, border: '1px dashed #e5e7eb', borderRadius: 12 }}>
          No receipts yet. Create one from the "Create Receipt" tab.
        </div>
      )}

      {!loading && receipts.length > 0 && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Receipt #', 'Date', 'Client', 'Bills', 'Total Settled', 'Cash Received', 'TDS', 'Credit Used', 'Wallet Credit', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: h === 'Total Settled' || h === 'Cash Received' || h === 'TDS' || h === 'Credit Used' || h === 'Wallet Credit' ? 'right' : 'left', fontWeight: 600, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {receipts.map((r, idx) => {
                const allocations = Array.isArray(r.billAllocations) ? r.billAllocations : [];
                return (
                  <tr key={r.id} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', background: r.status === 'reversed' ? '#fef2f2' : '#fff' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 700, color: '#6366f1' }}>{r.receiptNumber}</td>
                    <td style={{ padding: '11px 14px', color: '#6b7280' }}>{fmt(r.receiptDate)}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{r.companyName || r.customerName}</div>
                      {r.customerPhone && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.customerPhone}</div>}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#374151' }}>
                      {num(r.obAllocatedAmount) > 0 && (
                        <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>Opening Balance: {money(r.obAllocatedAmount)}</div>
                      )}
                      {allocations.map(a => (
                        <div key={a.billId} style={{ fontSize: 11, color: '#6b7280' }}>{a.billNumber}: {money(num(a.allocatedAmount) + num(a.tdsAmount))}{num(a.tdsAmount) > 0 ? ` (incl. TDS ${money(a.tdsAmount)})` : ''}</div>
                      ))}
                    </td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 700 }}>{money(num(r.totalAmount) + num(r.creditUsed) + num(r.tdsAmount))}</td>
<td style={{ padding: '11px 14px', textAlign: 'right', color: '#6b7280', fontWeight: 600 }}>{money(r.totalAmount)}</td>
<td style={{ padding: '11px 14px', textAlign: 'right', color: '#b45309', fontWeight: 600 }}>{num(r.tdsAmount) > 0 ? <span title={r.tdsSection ? `Section ${r.tdsSection}` : ''}>{money(r.tdsAmount)}{r.tdsPercent != null ? <span style={{ fontSize: 11, color: '#9ca3af' }}> · {Number(Number(r.tdsPercent).toFixed(2))}%</span> : null}</span> : '—'}</td>
<td style={{ padding: '11px 14px', textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>{num(r.creditUsed) > 0 ? money(r.creditUsed) : '—'}</td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', color: '#d97706', fontWeight: 600 }}>{num(r.surplusCredit) > 0 ? `+${money(r.surplusCredit)}` : '—'}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: r.status === 'reversed' ? '#fee2e2' : r.status === 'converted' ? '#fef3c7' : '#dcfce7',
                        color:      r.status === 'reversed' ? '#b91c1c' : r.status === 'converted' ? '#b45309' : '#16a34a' }}>
                        {r.status === 'reversed' ? 'Reversed' : r.status === 'converted' ? 'Converted' : 'Active'}
                      </span>
                      {r.editCount > 0 && (
                        <span title={`Last edited ${fmt(r.lastEditedAt)}`}
                          style={{ marginLeft: 6, padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#f3f4f6', color: '#4b5563' }}>
                          Edited
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      {r.status === 'active' && isAdmin && num(r.obAllocatedAmount) <= 0 && (
                        <button onClick={() => setEditingId(r.id)}
                          style={{ padding: '5px 10px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FiEdit2 size={12} /> Edit
                        </button>
                      )}
                      {r.status === 'active' && (
                        <button onClick={() => handleReverse(r)} disabled={reversing === r.id}
                          style={{ padding: '5px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {reversing === r.id ? '…' : 'Reverse'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingId && (
        <EditReceiptModal
          receiptId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { load(); }}
        />
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>‹ Prev</button>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer' }}>Next ›</button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Main AdminBilling Page  (updated to include receipt tabs)
   ═══════════════════════════════════════════════════════════════════ */

const TABS = [
  { id: 'generate', label: 'Generate Bill',   icon: <FiPlus size={15} /> },
  { id: 'all',      label: 'All Bills',        icon: <FiFileText size={15} /> },
  { id: 'receipt',  label: 'Create Receipt',   icon: <MdCurrencyRupee size={15} /> },
  // { id: 'receipts', label: 'All Receipts',     icon: <FiList size={15} /> },
];

const AdminBilling = () => {
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(window.location.search).get('tab') || 'generate'
  );
  const [refreshTrigger, setRefresh]   = useState(0);
  const [selectedCustomer, setCustomer] = useState(null);

  const handleBillCreated = () => {
    setRefresh(r => r + 1);
    setActiveTab('all');
  };

  const handleReceiptCreated = () => {
    setRefresh(r => r + 1);
    setActiveTab('receipts');
  };

  return (
    <div className="so-page ab-billing-page">
      <div className="so-page-head">
        <div><h2>Tax Invoice</h2></div>
      </div>

      <div className="ab-top-bar">
        {/* Customer combobox only relevant for bill tabs */}
        {(activeTab === 'generate' || activeTab === 'all') && (
          <div className="ab-top-bar__left">
            <CustomerCombobox
              selectedCustomer={selectedCustomer}
              onSelect={setCustomer}
              activeTab={activeTab}
            />
          </div>
        )}

        <div className="ab-top-bar__tabs" style={{ marginLeft: 'auto' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`ab-tab-btn${activeTab === t.id ? ' ab-tab-btn--active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ab-content">
        {activeTab === 'generate' && (
          <GenerateBillSection onBillCreated={handleBillCreated} selectedCustomer={selectedCustomer} />
        )}
        {activeTab === 'all' && (
          <AllBillsSection refreshTrigger={refreshTrigger} selectedCustomer={selectedCustomer} />
        )}
        {activeTab === 'receipt' && (
          <CreateReceiptSection onReceiptCreated={handleReceiptCreated} />
        )}
        {activeTab === 'receipts' && (
          <AllReceiptsSection refreshTrigger={refreshTrigger} />
        )}
      </div>
    </div>
  );
};

export default AdminBilling;